import React from "react";
import { useState } from "react";

import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  Image,
  ScrollView,
} from "react-native";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { StackActions, useNavigation } from "@react-navigation/native";
import { navigateToScreen } from "../../../../utility/NavigationHelper";
import { CommonButton } from "../../../../common/CommonButton";
import { AppColors } from "../../../../utility/AppColors";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import { AppImages } from "../../../../utility/AppImages";
import ImageComponent from "../../../../Components/ImageComponent";
import { AcceptEventChangesByOrganizer } from "../../../../services/notification";
import { ActivityIndicator } from "react-native-paper";
import { AppScreens } from "../../../Utility/AppScreens";
import { Base64 } from "js-base64";
import { toast } from "react-toastify";
import { useUserData } from "../../../../context/UserDataContext";
import { base_url, endpoints } from "../../../../Api/configs";
import { storeUserActivity, ACTIVITY_TYPES } from "../../../../services/activityService";

interface EventsProps {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  eventID: string;
  onClose: () => void;
  updatedEventDetails: any;
  previousEventDetails: any;
  notificationId: string;
  accessToken: string;
  updateNotificationStatus?: (
    notificationId: string,
    status: "accepted" | "rejected"
  ) => void;
}

const fieldsToCheck = [
  "title",
  "startDateTime",
  "gameTitle",
  "gamerID",
  "device",
  "eventImageUrl",
  "description",
];

const EventsDetailsUpdatedModal: React.FC<EventsProps> = ({
  showModal = false,
  setShowModal,
  eventID,
  onClose,
  updatedEventDetails,
  previousEventDetails,
  notificationId,
  accessToken,
  updateNotificationStatus,
}) => {
  const navigation = useNavigation();
  const styles = useStyles();
  const deviceSize = useDeviceSize();
  const { userData } = useUserData();
  const [isLoading, setIsLoading] = useState<"accept" | "reject" | null>(null);
  const [isNegotiating, setIsNegotiating] = useState(false);
  const onCloseClick = () => {
    setShowModal(false);
    onClose();
  };

  const handleEventUpdateResponse = async (action: boolean) => {
    setIsLoading(action ? "accept" : "reject");
    try {
      await AcceptEventChangesByOrganizer(
        accessToken,
        notificationId,
        action
      );

      // Update the notification status in the UI without refreshing
      if (updateNotificationStatus) {
        updateNotificationStatus(
          notificationId,
          action ? "accepted" : "rejected"
        );
      }

      // Show success message
      toast.success(
        action
          ? "Changes accepted successfully"
          : "Changes rejected successfully"
      );
    } catch (error) {
      console.error(
        `Failed to ${action ? "accept" : "reject"} event changes:`,
        error
      );
      toast.error(
        `Failed to ${action ? "accept" : "reject"} changes. Please try again.`
      );
    } finally {
      setIsLoading(null);
      // Always close the modal, regardless of success or failure
      setShowModal(false);
      onClose();
    }
  };

  const onAcceptChanges = () => handleEventUpdateResponse(true);
  const onRejectChanges = () => handleEventUpdateResponse(false);

  const changedFields = Object.keys(updatedEventDetails).filter((field) => {
    const prevField = field === "title" ? "eventTitle" : field;
    return (
      fieldsToCheck.includes(field) &&
      JSON.stringify(updatedEventDetails[field]) !==
      JSON.stringify(previousEventDetails[prevField])
    );
  });

  console.log("Changed Fields:", changedFields);

  const fieldDisplayNames = {
    title: "Event Title",
    startDateTime: "Start Date & Time",
    gameTitle: "Game Title",
    gamerID: "Gamer ID",
    device: "Device",
    eventImageUrl: "Event Image",
    description: "Description",
  };

  const formatValue = (
    field: string,
    value: any,
    isPrevious: boolean = false
  ) => {
    console.log("Field:", field, "Value:", value, "Is Previous:", isPrevious);

    if (field === "eventImageUrl") {
      const imageUrl = isPrevious ? previousEventDetails.eventImageUrl : value;
      if (imageUrl && typeof imageUrl === "string") {
        return (
          <Image
            source={{ uri: imageUrl }}
            style={{ width: 100, height: 50, resizeMode: "contain" }}
          />
        );
      }
      return <Text style={styles.summaryValue}>Not Set</Text>;
    }

    if (field === "title") {
      if (isPrevious) {
        const prevTitle = previousEventDetails.eventTitle;
        console.log("Previous eventTitle:", prevTitle);
        return prevTitle !== undefined && prevTitle !== null
          ? prevTitle
          : "Not Set";
      }
      return value !== undefined && value !== null ? value : "Not Set";
    }

    if (field === "startDateTime") {
      return value ? new Date(value).toLocaleString() : "Not Set";
    }
    if (field === "gamerID") {
      return value && value.organiser ? value.organiser : "Not Set";
    }
    return value !== undefined && value !== null ? value : "Not Set";
  };

  const handleChatRedirect = async () => {
    // Check if user is logged in
    if (!userData || !accessToken) {
      toast.info("Please log in to negotiate terms");
      setShowModal(false);
      navigation.dispatch(StackActions.replace(AppScreens.LoginScreen));
      return;
    }

    setIsNegotiating(true);
    try {
      // Call negotiate terms API
      const response = await fetch(
        `${base_url}${endpoints.events.negotiateTerms}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            accessToken,
            eventId: eventID || previousEventDetails?.eventID,
            newOpponentId: userData.username || userData.userID,
          }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        // Check if response has message indicating success
        if (result.message || result.chatId) {
          toast.success("Terms negotiated successfully");

          // Track activity FIRST before redirect (fire and forget)
          storeUserActivity(
            accessToken,
            eventID || previousEventDetails?.eventID,
            ACTIVITY_TYPES.NEGOTIATION_STARTED
          ).catch((err) => console.warn("Failed to track activity:", err));

          setShowModal(false);
          onClose();

          // Redirect to ongoing events chat tab
          navigation.dispatch(
            StackActions.replace(AppScreens.OnGoingEvents, {
              eventID: eventID || previousEventDetails?.eventID,
              tab: "Chat",
            })
          );
        } else {
          const errorMessage = result.error || "Failed to negotiate terms";
          toast.error(errorMessage);
        }
      } else {
        const errorMessage = result.error || "Failed to negotiate terms";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error negotiating terms:", error);
      toast.error("Failed to negotiate terms. Please try again.");
    } finally {
      setIsNegotiating(false);
    }
  };

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={showModal}
      onRequestClose={onCloseClick}
    >
      <View style={styles.container}>
        <View style={[styles.modalView, { marginVertical: 20 }]}>
          <ScrollView
            contentContainerStyle={[
              {
                flexGrow: 1,
                alignItems: "center",
                justifyContent: "center",
                alignSelf: "center",
                width: "100%",
              },
            ]}
            style={{ width: "100%" }}
            showsVerticalScrollIndicator={false}
          >
            {/* Header */}
            <View style={styles.header}>
              <Text style={styles.headerText}>Event Details Updated</Text>
              <TouchableOpacity
                style={styles.negotiate}
                onPress={handleChatRedirect}
                disabled={isNegotiating}
              >
                {isNegotiating ? (
                  <ActivityIndicator size="small" color={AppColors.white} />
                ) : (
                  <>
                    <ImageComponent
                      url={AppImages.icNotificationMessage}
                      style={{ width: 20, height: 20 }}
                    />
                    <Text
                      style={[
                        deviceSize === DEVICE_SIZES.SM ||
                          deviceSize === DEVICE_SIZES.XS
                          ? AppFontStyle.REGULAR_ANYBODY_13
                          : AppFontStyle.REGULAR_ANYBODY_16,
                        {
                          color: AppColors.white,
                          paddingTop:
                            deviceSize === DEVICE_SIZES.SM ||
                              deviceSize === DEVICE_SIZES.XS
                              ? 4
                              : 2,
                        },
                      ]}
                    >
                      Negotiate Terms
                    </Text>
                  </>
                )}
              </TouchableOpacity>
            </View>

            <View style={styles.separator} />

            <View style={styles.small}>
              <View style={styles.content}>
                <Image source={AppImages.icEventUpdated} style={styles.logo} />
                <Text style={styles.description}>
                  The organiser has made changes to the event details. Please
                </Text>
                <Text style={styles.description}>
                  review and either accept or reject the updates before the
                  event starts
                </Text>
              </View>

              <View style={styles.summaryContainer}>
                <View style={styles.summaryHeaderContainer}>
                  <Text style={styles.summaryHeader}>
                    Kaller made below Changes
                  </Text>
                </View>
                <View style={styles.separator} />

                <View style={styles.summaryRow}>
                  {changedFields.map((field) => (
                    <View key={field} style={styles.summaryBox}>
                      <Text style={styles.summaryTitle}>
                        {fieldDisplayNames[field]}
                      </Text>
                      <View style={styles.row}>
                        <Text
                          style={styles.summaryValue}
                          numberOfLines={2}
                          ellipsizeMode="tail"
                        >
                          {formatValue(
                            field,
                            previousEventDetails[
                            field === "title" ? "eventTitle" : field
                            ],
                            true
                          )}
                        </Text>
                        <Image
                          source={AppImages.icArrow}
                          style={styles.arrowIcon}
                        />
                        <Text
                          style={styles.summaryValue}
                          numberOfLines={2}
                          ellipsizeMode="tail"
                        >
                          {formatValue(
                            field,
                            updatedEventDetails[field],
                            false
                          )}
                        </Text>
                      </View>
                    </View>
                  ))}
                </View>
              </View>
            </View>

            <View style={styles.separator} />

            <View style={styles.buttonContainer}>
              <CommonButton
                title={
                  isLoading === "reject" ? "Rejecting..." : "❌ Reject Changes"
                }
                onTap={onRejectChanges}
                buttonStyle={[
                  styles.cancelButton,
                  isLoading === "accept" && styles.disabledButton,
                ]}
                textStyle={styles.cancelText}
                disabled={isLoading === "accept"}
                leftIcon={
                  isLoading === "reject" ? (
                    <ActivityIndicator size="small" color={AppColors.white} />
                  ) : null
                }
              />
              <CommonButton
                title={
                  isLoading === "accept" ? "Accepting..." : "✅ Accept Changes"
                }
                onTap={onAcceptChanges}
                buttonStyle={[
                  styles.submitButton,
                  isLoading === "reject" && styles.disabledButton,
                ]}
                textStyle={styles.submitText}
                disabled={isLoading === "reject"}
                leftIcon={
                  isLoading === "accept" ? (
                    <ActivityIndicator size="small" color={AppColors.white} />
                  ) : null
                }
              />
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const useStyles = CreateResponsiveStyle(
  {
    container: {
      flex: 1,
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      justifyContent: "center",
      alignItems: "center",
    },
    disabledButton: {
      backgroundColor: "gray",
      borderRadius: 10,
      height: 38,
      paddingHorizontal: 10,
      shadowOpacity: 0,
      paddingVertical: 5,
    },
    modalView: {
      width: "75%",
      backgroundColor: "#18151B",
      borderRadius: 12,
      padding: 20,
      alignItems: "center",
    },
    negotiate: {
      borderWidth: 1,
      borderColor: AppColors.white,
      borderRadius: 3,

      right: 0,
      top: 0,
      zIndex: 1,
      padding: 7,
      margin: 5,
      display: "flex",
      flexDirection: "row",
      gap: 7,
    },

    small: {
      backgroundColor: AppColors.purple_453B50,
      padding: 16,
      borderRadius: 24,
      marginBottom: 20,
      marginTop: 20,
      width: "100%",
    },
    header: {
      flexDirection: "row",
      width: "100%",
      justifyContent: "space-between",
      alignItems: "center",
    },
    headerText: {
      ...AppFontStyle.BOLD_ANYBODY_24,
      color: AppColors.white,
    },
    closeIcon: {
      height: 28,
      width: 28,
    },
    separator: {
      width: "100%",
      height: 2,
      backgroundColor: "#333",
      marginTop: 5,
    },
    content: {
      alignItems: "center",
      marginVertical: 15,
    },
    logo: {
      height: 145,
      width: 145,
      marginBottom: 10,
    },
    description: {
      ...AppFontStyle.REGULAR_ANYBODY_24,
      color: "#FFFFFF",
      textAlign: "center",
      marginBottom: 5,
    },
    summaryContainer: {
      backgroundColor: "#18151B",
      padding: 15,
      borderRadius: 10,
      width: "100%",
      marginVertical: 15,
    },
    summaryHeaderContainer: {
      backgroundColor: "#18151B",
      padding: 5,
      borderTopLeftRadius: 10,
      borderTopRightRadius: 10,
    },
    summaryHeader: {
      ...AppFontStyle.BOLD_ANYBODY_20,
      color: AppColors.white,
    },
    summaryRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 8,
      paddingTop: 10,
    },
    summaryBox: {
      backgroundColor: AppColors.purple_453B50,
      width: "19.3%",
      padding: 10,
      borderRadius: 8,
      marginBottom: 8,
    },
    summaryTitle: {
      ...AppFontStyle.BOLD_ANYBODY_14,
      color: AppColors.white,
      marginBottom: 5,
    },
    row: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
    },
    arrowIcon: {
      width: 12,
      height: 9.33,
      marginHorizontal: 5,
    },
    summaryValue: {
      ...AppFontStyle.BOLD_ANYBODY_12,
      color: "#B8B5C3",
      flexShrink: 1, // Allow text to shrink if needed
      flexWrap: "wrap",
      minWidth: 77,
    },
    buttonContainer: {
      flexDirection: "row",
      justifyContent: "space-between",
      width: "100%",
      marginTop: 10,
    },
    cancelButton: {
      backgroundColor: AppColors.white,
      borderRadius: 10,
      height: 38,
      paddingHorizontal: 10,
      shadowOpacity: 0,
      paddingVertical: 5,
    },
    cancelText: {
      color: AppColors.black,
      fontSize: 14,
    },
    submitButton: {
      backgroundColor: AppColors.buttonRed_DC2626,
      borderRadius: 10,
      height: 38,
      shadowOpacity: 0,
      paddingVertical: 0,
      paddingHorizontal: 10,
    },
    submitText: {
      color: AppColors.white,
      fontSize: 14,
    },
  },
  {
    [DEVICE_SIZES.MD]: { summaryBox: { width: "32.3%" } },
    [DEVICE_SIZES.LG]: { summaryBox: { width: "32.3%" } },
    [DEVICE_SIZES.SM]: {
      container: {
        flex: 1,
        backgroundColor: AppColors.blackTrans_000000a8,
        justifyContent: "center",
        alignItems: "center",
      },
      modalView: {
        width: "100%",
        backgroundColor: AppColors.black_121212,
        marginHorizontal: 30,
        alignSelf: "center",
        borderRadius: 12,
      },
      small: {
        backgroundColor: AppColors.purple_453B50,
        padding: 8,
        borderRadius: 24,
        marginBottom: 7,
        marginTop: 9,
        width: "100%",
      },
      negotiate: {
        borderWidth: 1,
        borderColor: AppColors.white,
        borderRadius: 3,

        right: 0,
        top: 0,
        zIndex: 1,
        padding: 4,
        margin: 5,
        display: "flex",
        flexDirection: "row",
        gap: 7,
      },
      content: {
        alignItems: "center",
        marginVertical: 0,
      },
      header: {
        flexDirection: "row",
        width: "100%",
        justifyContent: "space-between",
        alignItems: "center",
      },
      headerText: {
        ...AppFontStyle.BOLD_ANYBODY_16,
        color: AppColors.white,
      },
      logo: {
        height: 60,
        width: 60,
        marginBottom: 10,
      },
      description: {
        ...AppFontStyle.REGULAR_ANYBODY_14,
        color: "#FFFFFF",
        textAlign: "center",
        marginBottom: 0,
      },
      summaryContainer: {
        backgroundColor: "#18151B",
        padding: 10,
        borderRadius: 10,
        width: "100%",
        marginBottom: 5,
      },
      summaryHeader: {
        ...AppFontStyle.BOLD_ANYBODY_14,
        color: AppColors.white,
      },
      summaryRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "space-between",
      },
      summaryBox: {
        width: "48%",
        marginBottom: 8,
      },
      summaryTitle: {
        ...AppFontStyle.BOLD_ANYBODY_14,
        color: AppColors.white,
        marginBottom: 5,
        display: "flex",
        justifyContent: "center",
      },
      row: {
        flexDirection: "row",
        justifyContent: "center",
        display: "flex",
      },
      arrowIcon: {
        width: 12,
        height: 9.33,
        marginHorizontal: 5,
      },
      summaryValue: {
        ...AppFontStyle.BOLD_ANYBODY_12,
        color: "#B8B5C3",
        alignItems: "center",
        display: "flex",
        flexDirection: "row",
      },
      buttonContainer: {
        flexDirection: "row",
        justifyContent: "space-between",
        width: "100%",
      },
      cancelButton: {
        backgroundColor: AppColors.white,
        borderRadius: 10,
        height: 30,
        paddingHorizontal: 10,
        shadowOpacity: 0,
        paddingVertical: 5,
      },
      cancelText: {
        color: AppColors.black,
        fontSize: 12,
      },
      submitButton: {
        backgroundColor: AppColors.buttonRed_DC2626,
        borderRadius: 10,
        height: 38,
        shadowOpacity: 0,
      },
      submitText: {
        color: AppColors.white,
        fontSize: 12,
      },
    },

    [DEVICE_SIZES.XS]: {
      container: {
        flex: 1,
        backgroundColor: AppColors.blackTrans_000000a8,
        justifyContent: "center",
        alignItems: "center",
      },
      modalView: {
        width: "100%",
        backgroundColor: AppColors.black_121212,
        marginHorizontal: 0,
        alignSelf: "center",
        borderRadius: 12,
      },
      small: {
        backgroundColor: AppColors.purple_453B50,
        padding: 8,
        borderRadius: 24,
        marginBottom: 7,
        marginTop: 9,
        width: "100%",
      },
      negotiate: {
        borderWidth: 1,
        borderColor: AppColors.white,
        borderRadius: 3,

        right: 0,
        top: 0,
        zIndex: 1,
        padding: 4,
        margin: 5,
        display: "flex",
        flexDirection: "row",
        gap: 7,
      },
      content: {
        alignItems: "center",
        marginVertical: 0,
      },
      header: {
        flexDirection: "row",
        width: "100%",
        justifyContent: "space-between",
        alignItems: "center",
      },
      headerText: {
        ...AppFontStyle.BOLD_ANYBODY_16,
        color: AppColors.white,
      },
      logo: {
        height: 60,
        width: 60,
        marginBottom: 10,
      },
      description: {
        ...AppFontStyle.REGULAR_ANYBODY_14,
        color: "#FFFFFF",
        textAlign: "center",
        marginBottom: 0,
      },
      summaryContainer: {
        backgroundColor: "#18151B",
        padding: 10,
        borderRadius: 10,
        width: "100%",
        marginBottom: 5,
      },
      summaryHeader: {
        ...AppFontStyle.BOLD_ANYBODY_14,
        color: AppColors.white,
      },
      summaryRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 8,
        justifyContent: "space-between",
      },
      summaryBox: {
        width: "48%",
        marginBottom: 8,
      },
      summaryTitle: {
        ...AppFontStyle.BOLD_ANYBODY_14,
        color: AppColors.white,
        marginBottom: 5,
        display: "flex",
        justifyContent: "center",
      },
      row: {
        flexDirection: "row",
        justifyContent: "center",
        display: "flex",
      },
      arrowIcon: {
        width: 12,
        height: 9.33,
        marginHorizontal: 5,
      },
      summaryValue: {
        ...AppFontStyle.BOLD_ANYBODY_12,
        color: "#B8B5C3",
        alignItems: "center",
      },
      buttonContainer: {
        flexDirection: "row",
        justifyContent: "space-between",
        width: "100%",
      },
      cancelButton: {
        backgroundColor: AppColors.white,
        borderRadius: 10,
        height: 30,
        paddingHorizontal: 10,
        shadowOpacity: 0,
        paddingVertical: 5,
      },
      cancelText: {
        color: AppColors.black,
        fontSize: 12,
      },
      submitButton: {
        backgroundColor: AppColors.buttonRed_DC2626,
        borderRadius: 10,
        height: 38,
        shadowOpacity: 0,
      },
      submitText: {
        color: AppColors.white,
        fontSize: 12,
      },
    },
  }
);

export default EventsDetailsUpdatedModal;
